import React from 'react'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorMessage from '../ui/ErrorMessage'
import ProductGroupSelector from '../ui/ProductGroupSelector'
import { useKAMPerformance } from './kam-performance/useKAMPerformance'
import KAMFilters from './kam-performance/KAMFilters'
import KAMViewTabs from './kam-performance/KAMViewTabs'
import KAMRankingTable from './kam-performance/KAMRankingTable'
import KAMPerOperatorView from './kam-performance/KAMPerOperatorView'
import KAMPerKamView from './kam-performance/KAMPerKamView'
import DataFreshnessInfo from './kam-performance/DataFreshnessInfo'

const KAMPerformance = () => {
  const {
    // State
    loading,
    error,
    kamData,
    dataFreshness,
    freshnessThresholds,
    viewMode,
    selectedOperator,
    selectedMonth,
    selectedYear,
    selectedProductGroup,
    selectedKamName,
    perKamOperator,
    timeRangeType,
    customStartMonth,
    customStartYear,
    customEndMonth,
    customEndYear,
    perKamData,
    perKamLoading,
    exportLoading,
    
    // Setters
    setViewMode,
    setSelectedOperator,
    setSelectedMonth,
    setSelectedYear,
    setSelectedProductGroup,
    setSelectedKamName,
    setPerKamOperator,
    setTimeRangeType,
    setCustomStartMonth,
    setCustomStartYear,
    setCustomEndMonth,
    setCustomEndYear,
    
    // Actions
    fetchData,
    fetchPerKamData,
    handleExport,
    handlePerKamExport,
    
    // Computed
    getUniqueKamNames,
    getOperatorOptions,
    getKamsByOperator
  } = useKAMPerformance()

  if (loading) {
    return <LoadingSpinner message="Ładowanie danych wydajności KAM-ów..." />
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error}
        onRetry={fetchData}
      />
    )
  }

  const operatorOptions = getOperatorOptions()
  const groupedData = getKamsByOperator()
  const totalKamCount = kamData.filter(kam => !kam.is_unassigned).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div id="kam-performance-header" className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-1">
          <i className="fa-solid fa-trophy text-amber-400 mr-2"></i>
          Ranking Wydajności KAM-ów
        </h2>
        <p className="text-gray-500">
          Porównanie wydajności Key Account Managerów z realizacją celów sprzedażowych
        </p>
      </div>

      {/* Data Freshness Information */}
      <DataFreshnessInfo dataFreshness={dataFreshness} thresholds={freshnessThresholds} />

      {/* View Mode Tabs */}
      <KAMViewTabs 
        viewMode={viewMode} 
        onViewModeChange={setViewMode} 
      />

      {/* Filters - shown for general and by-operator views */}
      {(viewMode === 'general' || viewMode === 'by-operator') && (
        <div className="space-y-4">
          <KAMFilters
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            selectedOperator={selectedOperator}
            operatorOptions={operatorOptions}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            onOperatorChange={setSelectedOperator}
            showOperatorFilter={viewMode === 'general'}
          />
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <ProductGroupSelector
                operatorCode={selectedOperator !== 'Wszyscy' ? selectedOperator : 'ORANGE'}
                value={selectedProductGroup}
                onChange={setSelectedProductGroup}
                showAll={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {!kamData.length ? (
        <div className="text-center py-8">
          <i className="fa-solid fa-users text-gray-400 text-4xl mb-4"></i>
          <p className="text-gray-500">Brak danych wydajności KAM-ów do wyświetlenia.</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'general' && (
            <KAMRankingTable
              groupedData={groupedData}
              totalKamCount={totalKamCount}
              onExport={handleExport}
            />
          )}

          {viewMode === 'by-operator' && (
            <KAMPerOperatorView groupedData={groupedData} />
          )}

          {viewMode === 'per-kam' && (
            <KAMPerKamView
              selectedKamName={selectedKamName}
              perKamOperator={perKamOperator}
              timeRangeType={timeRangeType}
              customStartMonth={customStartMonth}
              customStartYear={customStartYear}
              customEndMonth={customEndMonth}
              customEndYear={customEndYear}
              operatorOptions={operatorOptions}
              uniqueKamNames={getUniqueKamNames()}
              perKamData={perKamData}
              perKamLoading={perKamLoading}
              exportLoading={exportLoading}
              onKamNameChange={setSelectedKamName}
              onOperatorChange={setPerKamOperator}
              onTimeRangeTypeChange={setTimeRangeType}
              onCustomStartMonthChange={setCustomStartMonth}
              onCustomStartYearChange={setCustomStartYear}
              onCustomEndMonthChange={setCustomEndMonth}
              onCustomEndYearChange={setCustomEndYear}
              onFetchData={fetchPerKamData}
              onExport={handlePerKamExport}
            />
          )}
        </>
      )}
    </div>
  )
}

export default KAMPerformance
